// @flow

import * as React from 'react';

import { CornerDownRight } from 'lucide-react';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  getCart,
  selectCartCurrency,
  removeProducts,
  useDiscountDetails,
  type ProductError as CartProductError,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';

import { useStandaloneMailboxPrice } from 'src/MailboxStandaloneCreate/modules/hooks/useProductPrice';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { ListRow } from '../../ListRow';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItemBase from './components/ProductItemBase';
import { TitleRow } from './components/Rows';
import { DiscountRow } from './components/Rows/DiscountRow';
import { MailboxPlanRows } from './components/Rows/MailboxPlanRows';
import TotalRow from './components/Rows/TotalRow';
import { useOnCounterChange } from './modules/mailbox';

export function MailboxStandAloneCreate({
  product,
}: {
  product: DecoratedProduct,
}): React.MixedElement {
  const dispatch = useDispatch();
  const { __, formatDate, formatPrice } = useTranslator();

  const [loading, setLoading] = React.useState(false);

  const currency = useAppSelector(selectCartCurrency);

  const discountDetails = useDiscountDetails({ product });

  const productErrors: Array<CartProductError> = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  const onCounterChange = useOnCounterChange(product, setLoading, loading);

  /**
   * Remove all mailboxes of a defined productType of the cart.
   */
  const removeFromCart = (e: any) => {
    e.preventDefault();

    if (product.quantity) {
      dispatch(removeProducts(product.ids.map(id => ({ id }))))
        .then(() => dispatch(getCart()))
        .catch(() => {
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          });
        });
    }
  };

  const priceByMonth = useStandaloneMailboxPrice({
    mailboxType: product.payload.config_name,
  });

  const durationMonth = product.payload.duration_month;

  // $FlowFixMe[incompatible-call] - durationMonth is defined
  const expirationDate = moment().add(durationMonth, 'months');

  const detail =
    !!priceByMonth &&
    __('%(montlyPrice)s/ month/ mailbox', {
      montlyPrice: formatPrice(priceByMonth, currency),
    });

  const priceCrossedOut = product.prices?.length
    ? product.computedTotalPrices?.beforeDiscount ||
      product.total?.before_discount
    : undefined;

  const price =
    product.computedTotalPrices?.beforeTaxes ?? product.total?.before_taxes;

  const info = __('Expires on %(dateEnd)s', {
    dateEnd: formatDate(expirationDate, 'LL'),
  });

  return (
    <ProductItemBase
      header={
        <>
          <TitleRow
            label={__('Mailbox')}
            productType={product.productType}
            handleRemoveAction={removeFromCart}
          />
          <ProductErrorItem errors={productErrors} productId={product.id} />

          <Base flex gap="spacing-2x" flexCol>
            <MailboxPlanRows
              product={product}
              onChange={onCounterChange}
              isLoading={loading}
            />
          </Base>
        </>
      }
      footer={
        <TotalRow
          hasTaxes={false}
          totalPrice={price}
          priceCrossedOut={priceCrossedOut}
          hasDiscount={!!discountDetails?.length}
        />
      }
    >
      {!!detail && (
        <ListRow
          intent="muted"
          icon={<CornerDownRight size={16} />}
          title={detail}
          options={info ? [info] : undefined}
          optionsArrow={false}
        />
      )}
      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${product.id}-${discount.label}`}
          label={discount.label}
          price={discount.price}
        />
      ))}
    </ProductItemBase>
  );
}
